import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/menu'
import { Link } from '@chakra-ui/next-js'
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  HStack,
  Stack,
  useDisclosure
} from '@chakra-ui/react'
import linkStyles from 'components/links/AnimatedLink/styles.module.scss'
import { Label } from 'components/typography'
import NavbarLinkContainer from 'layouts/LadingLayout/components/Navbar/components/NavbarLinkContainer'
import { navbarCourses, NavbarLink, navbarSexProducts } from 'layouts/LadingLayout/components/Navbar/navbarLinks'
import { useRouter } from 'next/router'
import { FC, useMemo } from 'react'

interface NavbarMenuProps {
  href?: string
  isActive?: boolean
  showCourses?: boolean
  showProducts?: boolean
  title: string
  items: NavbarLink[]
  solidNavbar?: boolean
}

const NavbarMenu: FC<NavbarMenuProps> = ({ title, items, isActive, href, showCourses, showProducts, solidNavbar }) => {
  const router = useRouter()
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <Menu {...{ isOpen, onOpen, onClose }}>
      <MenuButton
        shadow="none"
        sx={{
          'span::after': {
            transformOrigin: 'left center !important'
          }
        }}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        onClick={() => {
          href && router.push(href)
        }}
      >
        <HStack>
          <NavbarLinkContainer solidNavbar={solidNavbar} {...{ isActive }}>
            <Center h="72px">
              <Label>{title}</Label>
            </Center>
          </NavbarLinkContainer>
        </HStack>
      </MenuButton>
      <MenuList
        // this pseudo element prevents MenuList from closing when pointer is crossing the gap between button and MenuList
        _before={{
          content: '""',
          height: '8px',
          display: 'block',
          position: 'relative',
          top: '-8px'
        }}
        padding="0"
        border="none"
        bg="transparent"
        borderRadius="16px"
        position="relative"
        shadow="none"
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        backdropFilter={solidNavbar ? 'none' : 'blur(30px)'}
      >
        <Box
          bg={solidNavbar ? 'grayscale.20' : undefined}
          border="none"
          shadow="none"
          overflow="hidden"
          borderRadius="16px"
        >
          <Box>
            {items.map(({ label, href, isNew }) => (
              <NavbarMenuItem key={label} solidNavbar={solidNavbar} {...{ label, href }} />
            ))}
          </Box>
          {showCourses && (
            <Accordion allowToggle>
              <AccordionItem
                border="none"
                sx={{
                  'span::after': {
                    transformOrigin: 'left center !important'
                  }
                }}
              >
                <AccordionButton shadow="none !important" px="6" py="4" className={linkStyles.menuItem}>
                  <NavbarLinkContainer>
                    <Label color={solidNavbar ? 'phthalo' : 'alabaster'}>Cursos</Label>
                  </NavbarLinkContainer>
                </AccordionButton>
                <AccordionPanel>
                  <Stack>
                    {navbarCourses.map(({ label, href }) => (
                      <Link key={label} {...{ href }}>
                        <NavbarLinkContainer solidNavbar={solidNavbar} pl="4" fontWeight={400}>
                          <Label>{label}</Label>
                        </NavbarLinkContainer>
                      </Link>
                    ))}
                  </Stack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}
          {showProducts && (
            <Accordion allowToggle>
              <AccordionItem
                border="none"
                sx={{
                  'span::after': {
                    transformOrigin: 'left center !important'
                  }
                }}
              >
                <AccordionButton shadow="none !important" px="6" py="4" className={linkStyles.menuItem}>
                  <NavbarLinkContainer>
                    <Label color={solidNavbar ? 'phthalo' : 'alabaster'}>Produtos</Label>
                  </NavbarLinkContainer>
                </AccordionButton>
                <AccordionPanel>
                  <Stack>
                    {navbarSexProducts.map(({ label, href }) => (
                      <Link key={label} {...{ href }}>
                        <NavbarLinkContainer solidNavbar={solidNavbar} pl="4" fontWeight={400}>
                          {label}
                        </NavbarLinkContainer>
                      </Link>
                    ))}
                  </Stack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}
        </Box>
      </MenuList>
    </Menu>
  )
}

export default NavbarMenu

interface NavbarMenuItemProps extends NavbarLink {
  solidNavbar?: boolean
}

const NavbarMenuItem: FC<NavbarMenuItemProps> = ({ href, label, solidNavbar }) => {
  const router = useRouter()

  const isActive = useMemo(() => {
    return router.asPath === href
  }, [href, router.asPath])

  return (
    <MenuItem
      bg="transparent"
      p="0"
      onClick={() => {
        router.push(href)
      }}
    >
      <HStack w="full">
        <Link width="full" {...{ href }}>
          <Box p="12px 24px" _hover={{ background: 'blackAlpha.50' }}>
            <Label color={solidNavbar ? 'phthalo' : 'alabaster'}>{label}</Label>
          </Box>
        </Link>
      </HStack>
    </MenuItem>
  )
}
